import type { TeamNewsEventType } from '@/types/team-news.types';
import { TEAM_NEWS_DEFAULT_WINDOW_DAYS } from '@/services/team-news/constants';

import { EVENT_TYPE_LABEL } from './utils/getEventTypeConfig';

export const ALL_TAB = 'All';
export const ALL_CAT = 'all';
export const FOR_YOU_CAT = 'forYou';

/**
 * Where News preferences sends "For You" (LAB-3036). Home already opens on For
 * You whenever the member has For You stories (else All), so `feed=for-you`
 * selects nothing itself: it names the landing in the URL and pageviews.
 */
export const HOME_FOR_YOU_HREF = '/home?feed=for-you';

/**
 * Everything in the feed that is a conversation rather than a story: member
 * forum posts (the cards badged "Discussion"), plus news items that have a
 * linked forum thread.
 *
 * It used to be `active-discussions`, meaning only the second of those. That
 * left the forum-post cards with no pill at all while a similarly-named one sat
 * next to them doing something else — and because a non-`all` category dropped
 * forum posts entirely, clicking it actively hid the cards a member was after.
 * The id changed with the meaning so analytics can't silently average the two
 * definitions together.
 */
export const DISCUSSIONS_CAT = 'discussions';

export type TeamNewsCategoryId = typeof ALL_CAT | typeof FOR_YOU_CAT | typeof DISCUSSIONS_CAT | TeamNewsEventType;

export const FOR_YOU_CATEGORY = {
  id: FOR_YOU_CAT,
  label: 'For You',
} as const;

export const DISCUSSIONS_CATEGORY = {
  id: DISCUSSIONS_CAT,
  label: 'Discussions',
} as const;

export const CATEGORIES: Array<{ id: TeamNewsEventType | typeof ALL_CAT; label: string }> = [
  { id: ALL_CAT, label: 'All categories' },
  // Safe: EVENT_TYPE_LABEL is declared as an exact Record<TeamNewsEventType, string>,
  // so its keys are exactly TeamNewsEventType (Object.keys itself only returns string[]).
  ...(Object.keys(EVENT_TYPE_LABEL) as TeamNewsEventType[]).map((id) => ({ id, label: EVENT_TYPE_LABEL[id] })),
];

/**
 * The window the top-stories band ranks over, stated on the band itself so the
 * claim "top" is bounded by something the reader can see. Derived from the same
 * constant the grouped-by-focus-area fetch sends as `?windowDays=`, so the label
 * can't drift from the data behind it.
 */
export const TOP_STORIES_WINDOW_LABEL = `Last ${TEAM_NEWS_DEFAULT_WINDOW_DAYS} days`;

/** Set back to true to re-enable the rail/scroller "Popular this week" module. */
export const SHOW_POPULAR_THIS_WEEK = true;

export const SHOW_HIRING_NEWS = false;

/**
 * Personalized hiring roll-ups on the For You pill, per render.
 *
 * Its own knob rather than the cadence's `MAX_HIRING_ENTRIES`: that one bounds
 * the *unpersonalized* roll-ups on All (the ones `SHOW_HIRING_NEWS` gates), and
 * how many matched jobs a member should see is a different question from how
 * many generic ones the resting feed can carry. Raising this does not widen
 * that, and vice versa.
 *
 * Bounded above by the server: `GET /v1/job-openings/for-you` returns at most
 * `FOR_YOU_GROUP_LIMIT` roll-ups (10 at the time of writing), so a value beyond
 * that silently stops growing. Raise the server's limit first if this ever
 * needs to go higher.
 */
export const MAX_FOR_YOU_JOB_ENTRIES = 2;
